/**
 * 📱 Règle métier de référence pour les numéros de téléphone Befret (#1268).
 *
 * POURQUOI ce module existe
 * -------------------------
 * Six validations concurrentes cohabitaient : `simple-phone-input`
 * (libphonenumber strict mobile), `shared/validators/phone.validator.ts`
 * (libphonenumber + table de 6 indicatifs), `functions/src/utils/phone-normalize.ts`
 * (E.164 canonique), la validation shipment/receiver (longueur 6-15 caractères
 * seulement), les regex ad hoc d'auth/assisted/contact, et les heuristiques
 * d'indicatifs de `phone-prefix.util.ts`. Le même numéro pouvait donc obtenir
 * des verdicts différents selon la porte d'entrée.
 *
 * Ce module est l'UNIQUE autorité métier. Il ne dépend d'aucun framework :
 * Angular et les Cloud Functions le consomment tel quel.
 *
 * CE QU'IL NE FAIT PAS
 * --------------------
 * - Il n'impose PAS que le pays du numéro corresponde au pays de l'adresse.
 *   Un destinataire à Kinshasa joignable sur un mobile belge est un cas
 *   NORMAL du métier Befret (diaspora), pas une anomalie.
 * - Il ne décide pas seul de bloquer : c'est l'appelant qui choisit d'en
 *   faire une garde bloquante ou un simple signalement. Voir
 *   `docs/architecture/phone-validation.md`.
 */
// google-libphonenumber est un bundle CommonJS compilé par Closure : son
// `module.exports` n'expose AUCUN named export détectable par cjs-module-lexer
// (`import { PhoneNumberUtil } from 'google-libphonenumber'` lève une
// SyntaxError sous Node ESM). L'import par défaut est donc obligatoire pour que
// la sortie ESM du package reste chargeable par `node --test` et par Angular.
import libphonenumber from 'google-libphonenumber';
import { z } from 'zod';
const { PhoneNumberUtil, PhoneNumberFormat, PhoneNumberType } = libphonenumber;
const phoneUtil = PhoneNumberUtil.getInstance();
/** Type de ligne résolu par libphonenumber, exprimé en vocabulaire métier. */
export const PHONE_LINE_TYPES = [
    'mobile', 'fixed_line', 'fixed_or_mobile', 'voip', 'toll_free', 'premium_rate',
    'shared_cost', 'personal_number', 'pager', 'uan', 'voicemail', 'other', 'unknown',
];
export const phoneLineTypeSchema = z.enum(PHONE_LINE_TYPES);
/**
 * Code d'erreur stable. Les cinq premiers reprennent à l'identique les
 * `reason` de `validateE164` (functions) pour que la bascule ne change aucun
 * comportement serveur existant ; `invalid_prefix` est propre au contrat
 * `{ prefix, number }` des formulaires.
 */
export const PHONE_ERROR_CODES = [
    'empty', 'invalid_prefix', 'missing_country_code', 'parse_error', 'invalid_number', 'not_mobile',
];
export const phoneErrorCodeSchema = z.enum(PHONE_ERROR_CODES);
/** Erreur structurée, exploitable telle quelle par Angular (i18n) et le serveur (logs). */
export const phoneValidationErrorSchema = z
    .object({ code: phoneErrorCodeSchema, messageKey: z.string().min(1), message: z.string().min(1) })
    .strict();
export const phoneValidationResultSchema = z
    .object({
    valid: z.boolean(),
    e164: z.string().startsWith('+').nullable(),
    country: z.string().length(2).nullable(),
    countryCallingCode: z.string().regex(/^\d{1,4}$/).nullable(),
    nationalNumber: z.string().regex(/^\d+$/).nullable(),
    lineType: phoneLineTypeSchema,
    error: phoneValidationErrorSchema.optional(),
})
    .strict()
    .superRefine((result, context) => {
    if (result.valid && result.error) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['error'], message: 'A valid phone cannot contain an error' });
    }
    if (!result.valid && !result.error) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['error'], message: 'An invalid phone must contain an error' });
    }
});
export const phoneValidationOptionsSchema = z
    .object({ defaultRegion: z.string().length(2).optional(), mobileOnly: z.boolean().optional() })
    .strict();
/** Saisie brute : soit une chaîne complète, soit le couple des formulaires. */
export const phoneInputObjectSchema = z
    .object({ prefix: z.string().nullable().optional(), number: z.string().nullable().optional() })
    .strict();
export const phoneInputSchema = z.union([z.string(), z.null(), phoneInputObjectSchema]).optional();
const MESSAGES = {
    empty: { key: 'phone.error.required', message: 'Le numéro de téléphone est requis' },
    invalid_prefix: { key: 'phone.error.invalidPrefix', message: 'Indicatif international invalide' },
    missing_country_code: {
        key: 'phone.error.missingCountryCode',
        message: 'Indicatif international manquant (ex. +32)',
    },
    parse_error: { key: 'phone.error.unparsable', message: 'Numéro de téléphone illisible' },
    invalid_number: { key: 'phone.error.invalid', message: 'Numéro de téléphone invalide' },
    not_mobile: {
        key: 'phone.error.notMobile',
        message: 'Un numéro mobile est requis pour recevoir les notifications',
    },
};
function failure(code, partial = {}) {
    return {
        valid: false,
        e164: null,
        country: null,
        countryCallingCode: null,
        nationalNumber: null,
        lineType: 'unknown',
        ...partial,
        error: { code, messageKey: MESSAGES[code].key, message: MESSAGES[code].message },
    };
}
function mapLineType(type) {
    switch (type) {
        case PhoneNumberType.MOBILE:
            return 'mobile';
        case PhoneNumberType.FIXED_LINE:
            return 'fixed_line';
        case PhoneNumberType.FIXED_LINE_OR_MOBILE:
            return 'fixed_or_mobile';
        case PhoneNumberType.VOIP:
            return 'voip';
        case PhoneNumberType.TOLL_FREE:
            return 'toll_free';
        case PhoneNumberType.PREMIUM_RATE:
            return 'premium_rate';
        case PhoneNumberType.SHARED_COST:
            return 'shared_cost';
        case PhoneNumberType.PERSONAL_NUMBER:
            return 'personal_number';
        case PhoneNumberType.PAGER:
            return 'pager';
        case PhoneNumberType.UAN:
            return 'uan';
        case PhoneNumberType.VOICEMAIL:
            return 'voicemail';
        case PhoneNumberType.UNKNOWN:
            return 'unknown';
        default:
            return 'other';
    }
}
/**
 * Normalise un indicatif écrit sous l'une des formes historiquement persistées :
 * `'+32'`, `'32'`, `'BE'`, `'Belgium (+32)'`. Retourne `''` si rien d'exploitable.
 *
 * Cette tolérance n'est pas cosmétique : `users/{uid}.country` stocke le LIBELLÉ
 * `'Belgium (+32)'` sur des profils historiques, valeur qui a fuité jusque dans
 * le champ « préfixe » des commandes (cf. `core/utils/phone-prefix.util.ts`).
 */
export function normalizeDialCode(raw) {
    const value = String(raw ?? '').trim();
    if (!value)
        return '';
    if (value.startsWith('+')) {
        const digits = value.replace(/\D/g, '');
        return digits ? `+${digits}` : '';
    }
    const embedded = value.match(/\+(\d{1,4})/);
    if (embedded)
        return `+${embedded[1]}`;
    if (/^[A-Za-z]{2}$/.test(value)) {
        const code = countryCallingCodeForRegion(value.toUpperCase());
        return code ? `+${code}` : '';
    }
    if (/^\d{1,4}$/.test(value))
        return `+${value}`;
    return '';
}
/** Indicatif international d'une région ISO (`'BE'` → `32`), `null` si inconnue. */
export function countryCallingCodeForRegion(region) {
    try {
        const code = phoneUtil.getCountryCodeForRegion(String(region || '').toUpperCase());
        return code && code > 0 ? code : null;
    }
    catch {
        return null;
    }
}
/**
 * Région principale d'un indicatif (`'+32'` → `'BE'`, `'+1'` → `'US'`).
 * Remplace les tables `DIAL_CODE_TO_REGION` figées : libphonenumber connaît
 * tous les pays, pas seulement les six qui étaient listés à la main.
 */
export function regionForDialCode(dialCode) {
    const normalized = normalizeDialCode(dialCode);
    if (!normalized)
        return null;
    try {
        const region = phoneUtil.getRegionCodeForCountryCode(Number(normalized.slice(1)));
        // libphonenumber renvoie la sentinelle 'ZZ' pour un indicatif inconnu.
        return region && region !== 'ZZ' ? region : null;
    }
    catch {
        return null;
    }
}
/** Retire les séparateurs d'affichage sans toucher au `+` de tête. */
function stripSeparators(value) {
    return value.trim().replace(/[\s().–—-]/g, '');
}
/**
 * Construit les tentatives de lecture, de la plus fiable à la plus tolérante.
 *
 * L'ordre compte : on parse d'abord le numéro NATIONAL dans la région déduite
 * de l'indicatif, car c'est la seule lecture qui gère le préfixe interurbain.
 * Concaténer bêtement `+32` et `0470123456` produirait `+320470123456`, invalide,
 * alors que le numéro est parfaitement bon — et ces zéros de tête existent
 * réellement dans les documents historiques.
 */
function buildCandidates(prefixRaw, numberRaw, defaultRegion) {
    const dialCode = normalizeDialCode(prefixRaw);
    const hasPrefixInput = String(prefixRaw ?? '').trim().length > 0;
    const region = (dialCode && regionForDialCode(dialCode)) || defaultRegion?.toUpperCase();
    const compact = stripSeparators(numberRaw);
    const candidates = [];
    if (compact.startsWith('+'))
        candidates.push({ raw: compact });
    if (region)
        candidates.push({ raw: compact, region });
    if (dialCode && !compact.startsWith('+')) {
        candidates.push({ raw: `${dialCode}${compact.replace(/\D/g, '')}` });
    }
    // Indicatif DUPLIQUÉ : `{ prefix: 'CD', number: '243812345678' }`. Le champ
    // « numéro » de certains documents porte déjà l'indicatif, et le concaténer
    // donnerait `+243243…`. `simple-phone-input` neutralise déjà ce cas côté UI ;
    // on le neutralise ici pour que la relecture des documents historiques
    // aboutisse au même E.164 que la saisie d'origine.
    if (dialCode) {
        const dialDigits = dialCode.slice(1);
        const numericOnly = compact.replace(/\D/g, '');
        if (numericOnly.startsWith(dialDigits) && numericOnly.length > dialDigits.length) {
            candidates.push({ raw: `${dialCode}${numericOnly.slice(dialDigits.length)}` });
        }
    }
    return { candidates, hasPrefixInput, dialCode };
}
function describe(parsed) {
    const callingCode = parsed.getCountryCode();
    return {
        e164: phoneUtil.format(parsed, PhoneNumberFormat.E164),
        country: phoneUtil.getRegionCodeForNumber(parsed) || null,
        countryCallingCode: callingCode ? String(callingCode) : null,
        nationalNumber: parsed.getNationalNumber() ? String(parsed.getNationalNumber()) : null,
        lineType: mapLineType(phoneUtil.getNumberType(parsed)),
    };
}
/**
 * Valide et normalise un numéro métier. C'est LA fonction à appeler ;
 * tout le reste du module n'est que confort autour d'elle.
 */
export function validateBusinessPhone(input, options = {}) {
    const isPair = typeof input === 'object' && input !== null;
    const prefixRaw = isPair ? input.prefix : undefined;
    const numberRaw = String((isPair ? input.number : input) ?? '').trim();
    if (!numberRaw)
        return failure('empty');
    const defaultRegion = options.defaultRegion?.trim().toUpperCase() || undefined;
    const { candidates, hasPrefixInput, dialCode } = buildCandidates(prefixRaw, numberRaw, defaultRegion);
    if (hasPrefixInput && !dialCode)
        return failure('invalid_prefix');
    if (!candidates.length)
        return failure('missing_country_code');
    let lastCode = 'invalid_number';
    let bestInvalid = null;
    for (const candidate of candidates) {
        let parsed;
        try {
            parsed = phoneUtil.parseAndKeepRawInput(candidate.raw, candidate.region);
        }
        catch {
            lastCode = 'parse_error';
            continue;
        }
        // `isValidNumber` (et non `isValidNumberForRegion`) : le pays du numéro n'a
        // pas à coïncider avec celui de l'adresse — règle explicite du ticket.
        if (!phoneUtil.isValidNumber(parsed)) {
            lastCode = 'invalid_number';
            if (!bestInvalid) {
                try {
                    bestInvalid = describe(parsed);
                }
                catch {
                    bestInvalid = null;
                }
            }
            continue;
        }
        const described = describe(parsed);
        if (options.mobileOnly &&
            described.lineType !== 'mobile' &&
            described.lineType !== 'fixed_or_mobile') {
            return failure('not_mobile', described);
        }
        return { valid: true, ...described };
    }
    return failure(lastCode, bestInvalid ?? {});
}
/** `true` si le numéro passe la règle métier. Sucre pour les gardes booléennes. */
export function isBusinessPhoneValid(input, options = {}) {
    return validateBusinessPhone(input, options).valid;
}
/**
 * Forme canonique E.164, ou `null` si le numéro est invalide.
 * À utiliser AVANT toute persistance ou tout appel Twilio/DPD.
 */
export function normalizeBusinessPhone(input, options = {}) {
    const result = validateBusinessPhone(input, options);
    return result.valid ? result.e164 : null;
}
/**
 * Deux saisies désignent-elles le même abonné ? Compare les E.164 quand les
 * deux numéros sont valides, sinon retombe sur une comparaison des chiffres
 * significatifs — les documents historiques stockent le même numéro sous
 * `+32499896664`, `32499896664` ou `499896664`.
 */
export function businessPhonesMatch(left, right, options = {}) {
    const a = validateBusinessPhone(left, options);
    const b = validateBusinessPhone(right, options);
    if (a.valid && b.valid)
        return a.e164 === b.e164;
    const digits = (input) => {
        const isPair = typeof input === 'object' && input !== null;
        const raw = `${isPair ? (input.prefix ?? '') : ''}${(isPair ? input.number : input) ?? ''}`;
        return raw.replace(/\D/g, '');
    };
    const left8 = digits(left);
    const right8 = digits(right);
    if (left8.length < 8 || right8.length < 8)
        return false;
    return left8.endsWith(right8) || right8.endsWith(left8);
}
